import { ApolloClient } from '@apollo/client';

import { carrierRelationshipInAccountQuery } from './graphql';
import { fetchCompanyGroupRelationships } from '../company_group/fetch_relationships';
import {
  CarrierRelationshipInAccountQuery,
  CarrierRelationshipInAccountQueryVariables,
  CompanyRelationshipType,
  getNodes,
} from '../lib/graphql';

export interface GroupAccount {
  companyId: string;
  companyName: string;
  isHub: boolean;
}

interface Params {
  carrierId: string;
  targetCompanyId: string;
  relationshipType: CompanyRelationshipType;
}

export async function fetchGroupAccountsSharingCarrier(
  client: ApolloClient<object>,
  { carrierId, targetCompanyId, relationshipType }: Params,
): Promise<GroupAccount[]> {
  if (relationshipType !== CompanyRelationshipType.CARRIER && relationshipType !== CompanyRelationshipType.SUBCARRIER) {
    return [];
  }

  const [asMember, asHub] = await Promise.all([
    fetchCompanyGroupRelationships(client, { sourceCompanyId: targetCompanyId }),
    fetchCompanyGroupRelationships(client, { targetCompanyId }),
  ]);
  const hubId = asMember[0]?.targetCompany.company_id ?? (asHub.length > 0 ? targetCompanyId : undefined);
  if (hubId === undefined) {
    return [];
  }

  const links = await fetchCompanyGroupRelationships(client, { targetCompanyId: hubId });
  const accounts = new Map<string, GroupAccount>();
  const add = ({ company_id, company_name }: { company_id: string; company_name?: string | null }) =>
    accounts.set(company_id, {
      companyId: company_id,
      companyName: company_name ?? `#${company_id}`,
      isHub: company_id === hubId,
    });
  links.forEach(({ sourceCompany, targetCompany }) => {
    add(targetCompany);
    add(sourceCompany);
  });

  if (accounts.has(carrierId)) {
    return [];
  }

  const hasCarrier = await Promise.all(
    Array.from(accounts.values(), async (account) => {
      const { data } = await client.query<
        CarrierRelationshipInAccountQuery,
        CarrierRelationshipInAccountQueryVariables
      >({
        query: carrierRelationshipInAccountQuery,
        variables: { sourceCompanyId: carrierId, targetCompanyId: account.companyId, relationshipType },
        fetchPolicy: 'network-only',
      });
      return getNodes(data.companyRelationships).length > 0;
    }),
  );

  return Array.from(accounts.values()).filter((_, index) => hasCarrier[index]);
}
